import { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { useAuthStore } from '../store/auth';
import { getMe } from '../api/users';
import { ApiError } from '../api/client';

interface LayoutProps {
  title?: string;
}

export function Layout({ title }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const setAdmin = useAuthStore((s) => s.setAdmin);
  const clearAuth = useAuthStore((s) => s.clearAuth);

  // Refresh permissions/read-only flag on mount and on each route change so
  // changes made by a super_admin propagate without needing re-login.
  // Only a rejected token signs the user out (the API client already does that
  // on 401); a dropped connection or a slow 5xx keeps the cached profile.
  useEffect(() => {
    let cancelled = false;
    getMe()
      .then((r) => {
        if (!cancelled) setAdmin(r.admin);
      })
      .catch((err) => {
        if (!cancelled && err instanceof ApiError && err.status === 401) clearAuth();
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname, setAdmin, clearAuth]);

  // Close drawer on route change (mobile UX).
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    if (!sidebarOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [sidebarOpen]);

  return (
    <div className="min-h-screen bg-surface-sunken overflow-x-hidden">
      {/* Backdrop for mobile drawer */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="md:ml-60 flex flex-col min-h-screen">
        <Header title={title} onOpenSidebar={() => setSidebarOpen(true)} />
        <ReadOnlyBanner />

        <main className="flex-1 p-3 sm:p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function ReadOnlyBanner() {
  const admin = useAuthStore((s) => s.admin);
  if (!admin || admin.role === 'super_admin' || !admin.read_only) return null;
  return (
    <div className="bg-warning-soft border-b border-warning/30 text-warning text-sm px-4 py-2 text-center">
      Read-only mode — changes are disabled for your account.
    </div>
  );
}
